import { useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CreditCard, Loader2, Plus } from 'lucide-react';
import { Role } from '@nexa/auth';
import { RoleGate } from '@/auth/role-gate';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useCareBoard,
  useCaseCareCards,
  useIssueGroupVoucher,
} from '@/lib/disruptions/use-disruptions';
import { isLockConflictError } from '@/lib/locks/types';
import type { CareCardRow, DisruptionPnrGroupRow } from '@/lib/disruptions/types';
import { routingDisruptionUrn, type PnrGroupScope } from '@/lib/pnr/scope';
import { formatMinor } from '@/routes/disruptions/care-desk/care-desk-model';
import { BoardChip, FieldLabel } from '@/components/pnr/airport-ui';
import { VirtualCard, type CardStatus } from './virtual-card';
import { CardHistory } from './card-history';
import { IssueCardDialog } from './issue-card-dialog';
import { towerPath } from '@/routes/disruptions/operar/operate-types';

// The PNR's compensation card, shown as a card. This replaces the old
// amount-and-a-button MealVoucherCard: an operator handing money to a stranded
// passenger should see the instrument they are handing over, with its live
// balance, not a number in a form.
//
// ONE SOURCE. The card's facts come from the care board
// (`GET /v1/disruptions/:urn/care`, WS-merged by `CareChanged`) — the same read
// the Care desk table uses, so the drawer and the desk can never disagree. The
// roster row's `mealVoucher` denorm is used only for the ENTITLEMENT (what the
// passenger is owed before a card exists).
//
// The card itself is non-personalised: the passenger's identity lives in this
// panel, around the plastic, never embossed on it.

/** Care-board statuses that mean "a real card exists". */
const CARD_STATUS: Record<string, CardStatus> = {
  ACTIVE: 'ACTIVE',
  ISSUED: 'ACTIVE',
  PENDING: 'PENDING',
  BLOCKED: 'BLOCKED',
  DISABLED: 'DISABLED',
  FAILED: 'FAILED',
  FAILED_COMPENSATED: 'FAILED_COMPENSATED',
};

/** Why there is no card yet, in the operator's words. Holds KEYS, never language. */
const NO_CARD_REASON: Record<string, string> = {
  NOT_ISSUED: 'card.noCard.notIssued',
};

export function CompensationCardPanel({
  group,
  scope,
}: {
  group: DisruptionPnrGroupRow;
  scope: PnrGroupScope;
}) {
  const { t } = useTranslation('admin');
  const routingUrn = routingDisruptionUrn(scope, group);
  const board = useCareBoard(routingUrn);
  // STANDALONE case (no parent disruption ⇒ no care board): read the case's
  // wallet cards directly, or an issued card would stay invisible right after
  // the operator handed the money over.
  const caseCards = useCaseCareCards(routingUrn ? undefined : group.caseUrn);
  const issue = useIssueGroupVoucher(scope);
  const [error, setError] = useState<string | null>(null);
  const [lockedByName, setLockedByName] = useState<string | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);

  const boardRow = board.data?.cards.find((c) => c.subCaseUrn === group.subCaseUrn);
  const caseCard = caseCards.data?.find((c) => c.subCaseUrn === group.subCaseUrn);
  const row: CareCardRow | undefined =
    boardRow ??
    (caseCard
      ? {
          subCaseUrn: caseCard.subCaseUrn,
          pnrLabel: group.pnrLabel,
          holderMasked: group.leadName ? `${group.leadName.charAt(0)}****` : '—',
          ...(caseCard.last4 ? { last4: caseCard.last4 } : {}),
          ...(caseCard.network ? { network: caseCard.network } : {}),
          status: caseCard.status,
          currency: caseCard.currency,
          faceValueMinor: caseCard.faceValueMinor,
          toppedUpMinor: caseCard.toppedUpMinor ?? 0,
          ...(typeof caseCard.availableMinor === 'number'
            ? { availableMinor: caseCard.availableMinor }
            : {}),
          ...(caseCard.issuedAt ? { issuedAt: caseCard.issuedAt } : {}),
        }
      : undefined);
  const entitlement = group.mealVoucher;

  const status = row ? CARD_STATUS[row.status] : undefined;
  const hasCard = !!status;
  // Balances are only read when the wallet upstream answered; a card with no
  // balance read must say so rather than render a fabricated $0.00.
  const balancesKnown = board.data?.known.wallet !== false;

  const loadedMinor = row ? row.faceValueMinor + row.toppedUpMinor : undefined;
  const currency = row?.currency ?? entitlement?.currency ?? 'USD';
  const amountLabel = row
    ? formatMinor(loadedMinor ?? 0, currency)
    : typeof entitlement?.amountUsd === 'number'
      ? formatMinor(Math.round(entitlement.amountUsd * 100), currency)
      : undefined;
  const balanceLabel =
    row && balancesKnown && typeof row.availableMinor === 'number' && row.availableMinor !== loadedMinor
      ? formatMinor(row.availableMinor, currency)
      : undefined;
  // Spend is what was loaded minus what is left — the issuer reports the
  // balance, not the spend. Clamped: a pending hold must never read as negative.
  const spentMinor =
    typeof loadedMinor === 'number' && typeof row?.availableMinor === 'number'
      ? Math.max(0, loadedMinor - row.availableMinor)
      : 0;

  /** `discretionary` omitted = issue the engine's entitlement. */
  const runIssue = async (discretionary?: { amountUsd: number; reason: string }): Promise<void> => {
    setError(null);
    setLockedByName(null);
    try {
      // A STANDALONE case (no parent disruption) issues through the case-scoped
      // route — a policy-blocked manual case must still be able to compensate
      // its passengers (meals and other duty-of-care spend).
      const res = await issue.mutateAsync({
        subCaseUrn: group.subCaseUrn,
        caseUrn: group.caseUrn,
        ...(routingUrn ? { disruptionUrn: routingUrn } : {}),
        ...(discretionary ? { discretionary } : {}),
      });
      // The BFF answers 200 with a reason when a guard short-circuited — a
      // silent no-op would leave the operator thinking money moved.
      if (!res.issued) {
        setError(
          res.reason === 'already_issued'
            ? t('card.error.alreadyIssued')
            : t('card.error.notIssued', {
                reason: res.reason ?? t('card.error.unknownReason'),
              }),
        );
        return;
      }
      setIssueOpen(false);
    } catch (e) {
      if (isLockConflictError(e)) setLockedByName(e.holderName);
      else setError(t('card.error.issueFailed'));
    }
  };

  // The panel's ACTIONS live on its bar (the header's right edge), never among
  // the facts: issue a card while there is none (an entitled group in one click
  // at the engine's amount, anyone else through the discretionary dialog), and
  // the jump to the Care desk where top-ups, void and the money view live — one
  // home each. Issuing is a wallet OPS_SUPERVISOR write — gated honestly here
  // instead of letting an operator click into a "wallet 403". Rendered on EVERY
  // scope, including a standalone case with no parent disruption (case-scoped
  // issue route).
  const headerActions = (
    <div className="flex flex-wrap items-center gap-2" data-testid="drawer-compensation-actions">
      {entitlement?.includesLounge ? <BoardChip tone="info">{t('card.lounge')}</BoardChip> : null}
      {!hasCard && entitlement ? (
        <RoleGate min={Role.OPS_SUPERVISOR}>
          <Button
            size="sm"
            onClick={() => void runIssue()}
            disabled={issue.isPending}
            data-testid="drawer-issue-voucher"
          >
            {issue.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" aria-hidden />
            ) : (
              <CreditCard className="mr-1 h-3.5 w-3.5" aria-hidden />
            )}
            {t('card.issueAmount', { amount: amountLabel ?? t('card.issueWord') })}
          </Button>
        </RoleGate>
      ) : null}
      {!hasCard ? (
        <RoleGate min={Role.OPS_SUPERVISOR}>
          <Button
            size="sm"
            variant={entitlement ? 'outline' : 'default'}
            onClick={() => {
              setError(null);
              setIssueOpen(true);
            }}
            data-testid="drawer-issue-card-custom"
          >
            <CreditCard className="mr-1 h-3.5 w-3.5" aria-hidden />
            {entitlement ? t('card.differentAmount') : t('card.issueACard')}
          </Button>
        </RoleGate>
      ) : null}
      {routingUrn ? (
        <Button asChild size="sm" variant="outline">
          <Link
            to={`${towerPath(routingUrn)}#care?pnr=${encodeURIComponent(group.subCaseUrn)}`}
            data-testid="voucher-open-care-desk"
          >
            {hasCard ? (
              <>
                <Plus className="mr-1 h-3.5 w-3.5" aria-hidden /> {t('card.topUpInCareDesk')}
              </>
            ) : (
              t('card.openInCareDesk')
            )}
          </Link>
        </Button>
      ) : null}
    </div>
  );

  return (
    <section
      className="rounded-lg border border-border p-4"
      aria-label={t('card.title')}
      data-testid="drawer-compensation-card"
      {...(entitlement?.cardUrn ? { title: entitlement.cardUrn } : {})}
    >
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <FieldLabel className="flex items-center gap-1.5">
          <CreditCard className="h-3.5 w-3.5" aria-hidden />
          {t('card.title')}
        </FieldLabel>
        {headerActions}
      </div>

      {board.isLoading ? (
        <Skeleton className="aspect-[1.586] w-full max-w-[21rem] rounded-xl" />
      ) : (
        <VirtualCard
          {...(row?.network ? { network: row.network } : {})}
          {...(row?.last4 ? { last4: row.last4 } : {})}
          {...(status ? { status } : {})}
          {...(amountLabel ? { amountLabel } : {})}
          {...(balanceLabel ? { balanceLabel } : {})}
          reference={group.pnrLabel}
        />
      )}

      {/* Who it belongs to lives HERE, beside the plastic — never on it. */}
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs sm:grid-cols-3">
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {t('card.passenger')}
          </dt>
          <dd className="mt-0.5 font-medium">{row?.holderMasked ?? group.leadName ?? '—'}</dd>
        </div>
        <div>
          <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            {t('card.pnr')}
          </dt>
          <dd className="mt-0.5 font-mono font-medium">{group.pnrLabel}</dd>
        </div>
        {row?.issuedAt ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('card.issued')}
            </dt>
            <dd className="mt-0.5 tabular-nums">
              {new Date(row.issuedAt).toLocaleString(undefined, {
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </dd>
          </div>
        ) : null}

        {/* How much money is actually on the card — the question an operator is
            asked at the desk. Stated even when nothing has been spent, rather
            than only when the balance diverges from the loaded amount. */}
        {hasCard && typeof loadedMinor === 'number' ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('card.loaded')}
            </dt>
            <dd className="mt-0.5 tabular-nums" data-testid="compensation-card-loaded">
              {formatMinor(loadedMinor, currency)}
            </dd>
          </div>
        ) : null}
        {hasCard && (row?.toppedUpMinor ?? 0) > 0 ? (
          <div>
            <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              {t('card.toppedUp')}
            </dt>
            <dd className="mt-0.5 tabular-nums" data-testid="compensation-card-topped-up">
              {formatMinor(row?.toppedUpMinor ?? 0, currency)}
            </dd>
          </div>
        ) : null}
        {hasCard && balancesKnown && typeof row?.availableMinor === 'number' ? (
          <>
            <div>
              <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {t('card.available')}
              </dt>
              <dd
                className="mt-0.5 font-semibold tabular-nums"
                data-testid="compensation-card-available"
              >
                {formatMinor(row.availableMinor, currency)}
              </dd>
            </div>
            {spentMinor > 0 ? (
              <div>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                  {t('card.spent')}
                </dt>
                <dd className="mt-0.5 tabular-nums" data-testid="compensation-card-spent">
                  {formatMinor(spentMinor, currency)}
                </dd>
              </div>
            ) : null}
          </>
        ) : null}
      </dl>

      {row?.pendingTopUp ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          {t('card.topUpSettling')}
        </p>
      ) : null}

      {hasCard && !balancesKnown ? (
        <p className="mt-2 text-xs text-muted-foreground">{t('card.balancesUnknown')}</p>
      ) : null}

      {/* When, how much, who and why (care-card history) — every load on this card. */}
      {hasCard && routingUrn ? (
        <CardHistory
          disruptionUrn={routingUrn}
          subCaseUrn={group.subCaseUrn}
          caseUrn={group.caseUrn}
          originIata={group.origin}
          currency={currency}
        />
      ) : null}

      {/* No card yet. Any PNR can be given one — an entitled group in one click
          at the engine's amount, anyone else through the discretionary dialog. */}
      {!hasCard ? (
        <p className="mt-3 text-sm text-muted-foreground" data-testid="compensation-card-no-card">
          {t(
            (row ? NO_CARD_REASON[row.status] : undefined) ??
              (entitlement ? 'card.noCard.entitled' : 'card.noCard.none'),
          )}
        </p>
      ) : null}

      {lockedByName ? (
        <p
          className="mt-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400"
          role="status"
          aria-live="polite"
          data-testid="drawer-voucher-lock-conflict"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          <span>{t('card.lockConflict', { pnr: group.pnrLabel, name: lockedByName })}</span>
        </p>
      ) : null}
      {error ? (
        <p className="mt-2 text-xs text-destructive" role="alert" data-testid="drawer-voucher-error">
          {error}
        </p>
      ) : null}

      <IssueCardDialog
        open={issueOpen}
        pnrLabel={group.pnrLabel}
        paxCount={group.paxCount}
        presets={board.data?.knobs?.topUpPresets}
        pending={issue.isPending}
        error={error}
        onOpenChange={(next) => {
          setIssueOpen(next);
          if (!next) setError(null);
        }}
        onConfirm={(input) => void runIssue(input)}
      />
    </section>
  );
}
